(ns hai.tasks.add-pill
  "The tasks card's ＋ pill: the shared mini-app capsule
   ([[hai.mini-apps.add-pill]]) with the lane a new task starts in as its label,
   and a chevron beside the ＋ opening the menu that changes it. The pick is kept
   on the user (`::data/set-default-urgency`), so the ＋ names what it is about
   to do before it is pressed."
  (:require
   [hai.mini-apps.add-pill :as add-pill]
   [hai.tasks.data :as data]
   [hai.tasks.urgency :as urgency]
   [hai.ui.components :as c]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private chevron-icon (icons/mui-icon :outlined :expand_more))

;; The lane label runs Now / Next / Later, so the capsule would resize as the
;; pick moves along it — and the header's actions would shuffle with it. Hold it
;; to the widest.
(o/defstyled pill add-pill/pill
  ;; The deeper sage the tasks page fills its own Add task button with.
  {:background t/--task-add-fill}
  [:.add-action [:span {:min-width "38px" :text-align "left"}]])

(defn component
  "The tasks header's ＋ pill. `on-add` opens the host's composer — which the
   host seeds at [[hai.tasks.data/default-urgency]], the lane the label names.
   `container` is the element the menu portals into — the root the extension
   sidebar's portal-based UI shares ([[browser-ext.sidebar.shadow-root]]), as
   its other menus pass."
  [{:keys [on-add container]}]
  ;; Radix hands focus back to the trigger as the menu closes, and inside the
  ;; sidebar's injected frame the browser paints a focus ring on it — after a
  ;; click, which is the case `:focus-visible` exists to suppress and doesn't
  ;; here. So make that call ourselves: a menu opened by pointer skips the
  ;; restore, one opened by keyboard keeps it, since that is the one that needs
  ;; somewhere to return to.
  (r/with-let [pointer-opened? (atom false)]
    (let [default-urgency @(<s [::data/default-urgency])]
      [pill
       [tt/tooltip {:content (str "Add a task in " (urgency/label default-urgency))}
        [:button.add-action
         {:type "button"
          :aria-label (str "Add a task in " (urgency/label default-urgency))
          :on-click on-add}
         [icons/plus {:size 16}]
         [:span (urgency/label default-urgency)]]]
       [:span.add-divider]
       ;; Non-modal, as [[hai.ui.components.dropdown-menu]] is: a modal menu
       ;; locks body scroll, which in the sidebar means locking the page it is
       ;; injected into. Its touch guard comes along for the same reason — the
       ;; trigger opens on pointerdown, and the focus shift that follows reads as
       ;; an outside interaction to a non-modal dismiss layer, closing the menu
       ;; as it opens.
       [c/DropdownMenuRoot {:modal false}
        [c/DropdownMenuTrigger
         [:button.add-menu
          {:type "button"
           :aria-label "Change where new tasks start"
           :on-pointer-down (fn [_] (reset! pointer-opened? true))
           :on-key-down (fn [_] (reset! pointer-opened? false))}
          [chevron-icon]]]
        [c/DropdownMenuContent
         (cond-> {:align "end"
                  :onFocusOutside (fn [^js e] (.preventDefault e))
                  :onCloseAutoFocus (fn [^js e]
                                      (when @pointer-opened?
                                        (.preventDefault e)))}
           container (assoc :container container))
         [c/DropdownMenuRadioGroup
          {:value (name default-urgency)
           :onValueChange #(>e [::data/set-default-urgency (keyword "task.urgency" %)])}
          (for [{:keys [value label]} urgency/options]
            ^{:key value}
            [c/DropdownMenuRadioItem {:value (name value)}
             (str "Task in " label)])]]]])))
